import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Brain, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';

export default function KeyFindingsPage({ analysis, results }) {
  const [insights, setInsights] = useState(analysis?.ai_insights?.insights || '');
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(analysis?.ai_insights?.source || '');
  const flags = results?.trend_flags || [];

  const generateInsights = useCallback(async () => {
    if (!analysis?.id) {
      toast.error('Save an analysis first');
      return;
    }
    setLoading(true);
    try {
      const res = await analysesApi.getAiInsights(analysis.id);
      setInsights(res.data.insights || '');
      setSource(res.data.source || 'fallback');
      if (res.data.error) {
        toast.error('AI analysis had issues: ' + res.data.error);
      } else {
        toast.success('AI insights generated');
      }
    } catch (e) {
      console.error('AI insights error:', e);
      toast.error('Failed to generate insights');
    } finally {
      setLoading(false);
    }
  }, [analysis]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="findings-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Key Findings</h2>
          <p className="text-sm text-zinc-400 mt-1">AI-powered analysis and trend flags from all stages</p>
        </div>
        <Button
          onClick={generateInsights}
          disabled={loading || !analysis?.id}
          className="bg-indigo-600 hover:bg-indigo-500 text-white"
          data-testid="generate-ai-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Sparkles className="w-4 h-4 mr-2" />
          )}
          Generate AI Analysis
        </Button>
      </div>

      {/* AI Insights */}
      {insights && (
        <Card className="bg-zinc-900/60 border-indigo-500/30 ai-pulse">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Brain className="w-4 h-4 text-indigo-400" />
              <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">
                AI Deal Analysis
              </CardTitle>
              <Badge variant="outline" className="border-indigo-500/30 text-indigo-400 text-[10px]">
                {source === 'ai' ? 'Claude' : 'Calculated'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose prose-invert prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-sm text-zinc-300 leading-relaxed font-['DM_Sans']">
                {insights}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automated Trend Flags */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-300 font-['Manrope']">Automated Trend Flags</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {flags.map((flag, i) => (
              <div key={i} className={`flex items-start gap-3 p-3 rounded-lg border ${
                flag.type === 'positive' ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'
              }`}>
                <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${flag.type === 'positive' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className={`text-[10px] ${
                      flag.type === 'positive' ? 'border-emerald-500/30 text-emerald-400' : 'border-red-500/30 text-red-400'
                    }`}>
                      {flag.type === 'positive' ? 'Positive' : 'Negative'}
                    </Badge>
                    <span className="text-[10px] text-zinc-500 font-mono">{flag.category}</span>
                  </div>
                  <p className="text-sm text-zinc-300">{flag.message}</p>
                </div>
              </div>
            ))}
            {flags.length === 0 && (
              <p className="text-sm text-zinc-500">Import and calculate data to see automated trend flags.</p>
            )}
          </div>
        </CardContent>
      </Card>

      {!insights && !loading && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="p-8 text-center">
            <Brain className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">Click "Generate AI Analysis" to get a comprehensive deal assessment powered by Claude AI.</p>
            <p className="text-xs text-zinc-500 mt-1">Includes risk assessment, valuation opinion, and due diligence recommendations.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
